// Conversational fixer: the user describes corrections to already-posted journals
// in plain language; the engine proposes a batch of operations (update / delete /
// create) which the UI shows for approval before anything is written to Firestore.

import { authHeader } from './firebase';

const MODEL = 'claude-sonnet-5';
// Routed through our own backend so the provider key never reaches the browser.
const ENGINE_URL = (process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001') + '/api/engine';

const PROPOSE_TOOL = {
  name: 'propose_changes',
  description: 'Propose a batch of corrections to existing journals. The user will review and approve before anything is applied. Only call this when you have concrete changes; otherwise reply with plain text (e.g. to ask a clarifying question).',
  input_schema: {
    type: 'object',
    properties: {
      summary: { type: 'string', description: 'One-sentence summary of the overall change set.' },
      newAccounts: {
        type: 'array',
        description: 'New chart-of-accounts accounts to create before applying the operations. Only include an account that does not already exist and is genuinely needed. Reference the new code in the operations below.',
        items: {
          type: 'object',
          properties: {
            code: { type: 'string', description: 'A new, unused GL code in the correct numeric block (assets 1xxxxx, liabilities 2xxxxx, equity 3xxxxx, revenue/income 5xxxxx, cost of sales 6000xx, operating expenses 61xxxx).' },
            name: { type: 'string', description: 'Account name, e.g. "Subcontractor Costs".' },
            type: { type: 'string', enum: ['asset', 'liability', 'equity', 'revenue', 'cogs', 'expense', 'other_income', 'other_expense'], description: 'Account type — drives which statement it flows into.' },
            deductible: { type: 'boolean', description: 'For expense/cogs accounts: whether the expense is tax-deductible (usually true). Use null/omit for non-expense types.' },
            reason: { type: 'string', description: 'Why this account is being created.' },
          },
          required: ['code', 'name', 'type'],
        },
      },
      operations: {
        type: 'array',
        description: 'The journal operations to propose.',
        items: {
          type: 'object',
          properties: {
            op: { type: 'string', enum: ['update', 'delete', 'create'], description: 'update = replace an existing journal; delete = remove one; create = add a new one.' },
            id: { type: 'string', description: 'Existing journal id (REQUIRED for update and delete; must be a real id from the supplied journals).' },
            date: { type: 'string', description: 'ISO date YYYY-MM-DD (for update/create).' },
            narration: { type: 'string', description: 'Journal narration (for update/create).' },
            lines: {
              type: 'array',
              description: 'Full set of journal lines for update/create. Must balance: sum(dr) = sum(cr).',
              items: {
                type: 'object',
                properties: {
                  accountCode: { type: 'string' },
                  accountName: { type: 'string' },
                  dr: { type: 'number' },
                  cr: { type: 'number' },
                },
                required: ['accountCode', 'accountName', 'dr', 'cr'],
              },
            },
            reason: { type: 'string', description: 'Short human-readable explanation of why this change is being made.' },
          },
          required: ['op', 'reason'],
        },
      },
    },
    required: ['operations'],
  },
};

function buildSystem(project, coa) {
  const coaLines = coa.map((a) => '  ' + a.code + '  ' + a.name + '  (' + a.type + ')').join('\n');
  return (
    'You are Tlhakiso, a South African accounting assistant helping correct already-posted double-entry journals.\n' +
    'COMPANY: ' + project.name + (project.type ? ' (' + project.type + ')' : '') + '\n' +
    'TAX: ' + (project.taxType || 'Non-SBC') + ' at ' + (project.taxRate || 27) + '%\n\n' +
    'CHART OF ACCOUNTS (use ONLY these codes):\n' + coaLines + '\n\n' +
    'You will be given the current journals as JSON, each with an "id". You do two things:\n' +
    '  (A) ANSWER QUESTIONS about the books — analyse the journals and reply in plain text with specifics.\n' +
    '  (B) PROPOSE CORRECTIONS when the user asks for a change — via the propose_changes tool.\n\n' +
    'ANSWERING QUESTIONS:\n' +
    '- Each journal line shows an account "code" and a dr/cr amount; map codes to names/types using the chart of accounts above.\n' +
    '- Revenue/income accounts are those marked (revenue) or (income); expenses are (expense)/(cogs); etc.\n' +
    '- When asked what an amount "consists of" or "is made up of", list the individual journals that sum to it: date, narration/source description, account, and amount — then give the total. Show your working with real figures, never vague generalities.\n' +
    '- For totals (e.g. "total sales", "how much did we spend on X"), sum the relevant lines and state the figure and how many transactions it covers.\n' +
    '- If nothing matches, say so plainly. Do NOT call propose_changes just to answer a question — only use it when the user actually wants something changed.\n\n' +
    'CREATING ACCOUNTS:\n' +
    '- You CAN create a new chart-of-accounts account when a correction needs one that does not exist (e.g. a "Subcontractor Costs" expense). Add it to newAccounts, then use its code in the operations.\n' +
    '- Pick a new, unused code in the correct block and near related accounts (operating expenses are 61xxxx; e.g. a subcontractors expense could be 610110). Never reuse an existing code.\n' +
    '- type MUST be one of: asset, liability, equity, revenue, cogs, expense, other_income, other_expense. For expense/cogs set deductible (usually true).\n' +
    '- Prefer an existing account if one clearly fits; only create a new one when genuinely warranted or when the user explicitly asks for it.\n\n' +
    'PROPOSING CORRECTIONS:\n' +
    '1. When you have concrete changes, call propose_changes ONCE with every operation needed (including any newAccounts). Do not apply anything yourself — the user approves first.\n' +
    '2. For update/delete, the "id" MUST be a real id from the supplied journals. Never invent ids or journals.\n' +
    '3. Every update/create entry MUST balance: sum(dr) = sum(cr). Amounts are positive in either dr or cr, never negative, 2 decimals.\n' +
    '4. To RE-CATEGORISE, keep the bank line (asset code starting with 100) exactly as-is and only change the contra account code + name.\n' +
    '5. To EDIT an amount/date/narration, return the full corrected entry (keeping it balanced).\n' +
    '6. To SPLIT a transaction, return one update with multiple contra lines that still balance against the bank line.\n' +
    '7. Apply a change to ALL matching journals when the user implies "all" (e.g. "all Woolworths payments"). List each as its own operation.\n' +
    '8. If the request is ambiguous, or you cannot find matching journals, reply with plain text asking for clarification INSTEAD of calling the tool.\n' +
    '9. Give a short "reason" on every operation.'
  );
}

// Compact representation of a journal for the model (keeps token use down).
function compactJournal(j) {
  return {
    id: j.id,
    date: j.date,
    narration: j.narration,
    src: (j.source && j.source.description) || null,
    lines: (j.lines || []).map((l) => ({ code: l.accountCode, dr: Number(l.dr) || 0, cr: Number(l.cr) || 0 })),
  };
}

// history: [{ role: 'user'|'assistant', content: string }]
export async function askAssistant({ project, coa, journals, history }) {
  const compact = (journals || []).map(compactJournal);
  const messages = [
    { role: 'user', content: 'CURRENT JOURNALS (JSON):\n' + JSON.stringify(compact) },
    { role: 'assistant', content: 'I have the current journals loaded. What correction would you like to make?' },
    ...(history || []),
  ];

  const res = await fetch(ENGINE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: 8192,
      system: buildSystem(project, coa),
      tools: [PROPOSE_TOOL],
      tool_choice: { type: 'auto' },
      messages,
    }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error('API ' + res.status + ': ' + err.slice(0, 400));
  }

  const data = await res.json();
  const blocks = data.content || [];
  const toolUse = blocks.find((b) => b.type === 'tool_use' && b.name === 'propose_changes');
  const textBlocks = blocks.filter((b) => b.type === 'text').map((b) => b.text).join('\n').trim();

  if (toolUse) {
    const input = toolUse.input || {};
    return { text: textBlocks || input.summary || '', operations: input.operations || [], newAccounts: input.newAccounts || [], summary: input.summary || '' };
  }
  return { text: textBlocks || '(no response)', operations: [], newAccounts: [] };
}
